import { inject, Injectable } from '@angular/core';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';
import { User } from '../models';
import { AppService } from './app.service';
import { UserService } from './user.service';

export enum FeatureFlags {
  BYPASS_PROFILE_STATUS = 'bypass-profile-status',
  BYPASS_REAL_ESTATE_ROLE = 'bypass-real-estate-role',
  LOAN_OFFICER = 'gra-loan-officer',
  VIP_PROFILE = 'vip-flag',
  PHOTO_COLOR_VALIDATION = 'photo-color-validation',
  QR_CODE_DISPLAY = 'qr-code-display',
  BUY_SIDE_COMMISSION = 'buy-side-commission',
  ADDITIONAL_QTY = 'additional-qty',
  PRODUCT_REORDER = 'product-reorder',
  DYNAMIC_WEBSITE_PRODUCT = 'dynamic-website-product',
  CLIENT_REPORT_OPEN_HOUSE_AND_SHOWINGS = 'client-report-open-house-and-showings',
  SHOW_TASKS = 'show-tasks',
  SAVED_FILTERS = 'saved-filters',
  SUPER_ADMIN = 'super-admin',
  CUSTOM_YOUTUBE_AD_URL = 'custom-youtube-ad-url',
  RESUBMIT_REQUEST_PRODUCT_STATE = 'resubmit-request-product-state',
  OMNI_CHAT = 'omni-chat',
  AI_PROPERTY_DESCRIPTION = 'ai-prop-description-generation',
  AI_IMAGE_TAGGING = 'ai-photo-tag-generation',
  RESPONSIVE_VIEWS = 'responsive-views',
  THE_STUDIO = 'the-studio',
  AIHeadshotPro = 'ai-headshot-pro',
  PACKAGE_ADMIN = 'package-admin',
  UPLOAD_LISTING_VIDEO = 'upload-listing-video',
  HOME_WARRANTY_ADDON_POPUP_INTERVAL = 'home-warranty-popup-interval-in-days',
  ROTATE_PHOTOS = 'rotate-photos',
  SHOW_YEAR_BUILT_HOA = 'show-year-built-and-hoa',
  SHOW_ADD_TO_ORDER = 'show-add-to-order',
  SHOW_COMING_SOON = 'show-coming-soon',
}

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  private httpClient = inject(HttpClient);
  private readonly userService = inject(UserService);

  /** BehaviorSubjects for each of the configured/requested feature flags. */
  private readonly featureFlags$: { [flagName: string]: BehaviorSubject<any> } = {};

  async initialize() {
    if (AppService.get('environment') === 'local') {
      const flags = AppService.get('featureFlags') || [];
      flags.forEach((flag) => {
        if (typeof flag === 'string') {
          this.updateFlagValue(flag, true);
        } else if (Array.isArray(flag)) {
          this.updateFlagValue(flag[0], flag[1]);
        } else {
          this.updateFlagValue(flag.name, flag.value);
        }
      });
      return;
    }

    const user = this.userService.getCurrentUser();
    if (!user) {
      return;
    }

    const hmac = await this.getUsersHMAC();
    const ldUser = this.convertToLDUser(user);

    const clientId = AppService.get('launchDarklyClientID');

    const client = LaunchDarkly.initialize(clientId, ldUser, {
      hash: typeof hmac === 'object' ? hmac.hmac : hmac,
      useReport: true,
      // We only use LaunchDarkly for feature flags. Disabling goals avoids evaluating
      // remote A/B testing URL regexes in the browser.
      fetchGoals: false,
      streamReconnectDelay: 3000,
      inspectors: [
        {
          type: 'flag-used',
          name: 'dd-inspector',
          method: (key: string, detail: LaunchDarkly.LDEvaluationDetail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ],
    });

    await client.waitUntilReady();

    this.initializeFlags(client);
  }

  /*
   * Returns the feature value from the LaunchDarkley client
   * @param flag The FeatureFlag value we are returning
   */
  getFeature$<TExpected = any>(
    feature: FeatureFlags | string,
    defaultValue: TExpected = null,
  ): Observable<TExpected> {
    return this.getFeatureFlag$(feature, defaultValue).asObservable();
  }

  /**
   * Returns the feature value from the LaunchDarkley client
   * @param flag The FeatureFlag value we are returning
   */
  getFeature<TExpected = any>(
    feature: FeatureFlags | string,
    defaultValue: TExpected = null,
  ): TExpected {
    return this.getFeatureFlag$(feature, defaultValue).value;
  }

  /**
   * Checks to see if the feature flag is enabled.
   * @param flag Whether the flag is enabled or not. default is false
   */
  isFeatureEnabled$(flag: FeatureFlags, defaultValue: any = false): Observable<boolean> {
    return this.getFeatureFlag$(flag, defaultValue).asObservable();
  }

  /**
   * @deprecated Use isFeatureCurrentlyEnabled instead. The returned promise is always resolved.
   *
   * Checks to see if the feature flag is enabled.
   * @param flag Whether the flag is enabled or not. default is false
   */
  async isFeatureEnabled(flag: FeatureFlags, defaultValue: any = false): Promise<boolean> {
    return this.isFeatureCurrentlyEnabled(flag, defaultValue);
  }

  isFeatureCurrentlyEnabled(flag: FeatureFlags, defaultValue: any = false): boolean {
    return !!this.getFeatureFlag$(flag, defaultValue).value;
  }

  private getFeatureFlag$<TExpected = any>(
    feature: FeatureFlags | string,
    defaultValue: TExpected = null,
  ): BehaviorSubject<TExpected> {
    if (!this.featureFlags$[feature]) {
      // TODO: Setting the value of the subject to defaultValue is problematic
      // since the first caller checking on a given FF will determine the value for all subsequent callers
      this.featureFlags$[feature] = new BehaviorSubject(defaultValue);
    }
    return this.featureFlags$[feature];
  }

  /**
   * Stores the value for the given feature flag in the BehaviorSubject to be emitted
   * @param featureFlagName The feature flag name
   * @param value The value to emit
   */
  private updateFlagValue(featureFlagName: string, value: any) {
    if (this.featureFlags$[featureFlagName] == null) {
      this.featureFlags$[featureFlagName] = new BehaviorSubject<any>(value);
    } else {
      this.featureFlags$[featureFlagName].next(value);
    }
  }

  /** Initializes the current flag values after initialization */
  private initializeFlags(client: LaunchDarkly.LDClient) {
    const currentFlags = client.allFlags();
    Object.keys(currentFlags).forEach((key) => {
      const value: any = currentFlags[key];
      this.updateFlagValue(key, value);
    });

    // Subscribe to flag changes
    client.on('change', (keys) => this.onFlagChanges(keys));
  }

  /** Takes the flag changes (keys) and updates the current values */
  private onFlagChanges(keys: any) {
    // console.log('Change detected', keys)
    Object.keys(keys).forEach((key) => {
      const change: { current: any; previous: any } = keys[key];
      this.updateFlagValue(key, change.current);
    });
  }

  /** Converts the current user to the LaunchDarkly user */
  private convertToLDUser(user: User): LaunchDarkly.LDUser {
    return {
      key: user._id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      custom: {
        metroNumber: user?.profile?.office?.metro?.metroNumber || '',
        metroName: user?.profile?.office?.metro?.metroName || '',
        officeName: user?.profile?.office?.address?.name || '',
        officeCode: user?.profile?.office?.officeCode || '',
        marketingArea: user?.profile?.office?.marketingArea || '',
        appName: AppService.get('app'),
        productName: AppService.get('productSuiteName'),
        brandCode: user.identityIssuer?.includes('compass') ? 'CMP' : user.profile.office?.company?.brandCode ?? '',
      },
    };
  }

  /** Retrieves the HMAC from localStorage or from persist */
  private async getUsersHMAC(): Promise<any> {
    const cachedHMAC = localStorage.getItem('ld-hmac');
    if (cachedHMAC) {
      return cachedHMAC;
    }

    const route = `${AppService.get('persistBaseURL')}user/launch-darkly/hmac`;
    const res = (await firstValueFrom(this.httpClient.get(route))) as any;
    localStorage.setItem('ld-hmac', res.hmac);
    return res;
  }
}
